import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getBusinessAddresses,
  getBusinessAddress,
  createBusinessAddress,
  updateBusinessAddress,
  toggleBusinessAddressActive,
  deleteBusinessAddress,
} from './business-addresses.service';
import {
  TCreateBusinessAddress,
  TUpdateBusinessAddress,
} from '~/types/business-address';

export const BUSINESS_ADDRESS_KEYS = {
  all: ['business-addresses'] as const,
  lists: () => [...BUSINESS_ADDRESS_KEYS.all, 'list'] as const,
  list: (activeOnly: boolean) =>
    [...BUSINESS_ADDRESS_KEYS.lists(), { activeOnly }] as const,
  details: () => [...BUSINESS_ADDRESS_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...BUSINESS_ADDRESS_KEYS.details(), id] as const,
};

export const useGetBusinessAddresses = (activeOnly = false) => {
  return useQuery({
    queryKey: BUSINESS_ADDRESS_KEYS.list(activeOnly),
    queryFn: () => getBusinessAddresses(activeOnly),
  });
};

export const useGetBusinessAddress = (id: string | undefined) => {
  return useQuery({
    queryKey: BUSINESS_ADDRESS_KEYS.detail(id!),
    queryFn: () => getBusinessAddress(id!),
    enabled: !!id,
  });
};

export const useCreateBusinessAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TCreateBusinessAddress) => createBusinessAddress(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUSINESS_ADDRESS_KEYS.lists() });
    },
  });
};

export const useUpdateBusinessAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TUpdateBusinessAddress }) =>
      updateBusinessAddress(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: BUSINESS_ADDRESS_KEYS.lists() });
      queryClient.invalidateQueries({
        queryKey: BUSINESS_ADDRESS_KEYS.detail(variables.id),
      });
    },
  });
};

export const useToggleBusinessAddressActive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => toggleBusinessAddressActive(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: BUSINESS_ADDRESS_KEYS.lists() });
      queryClient.invalidateQueries({
        queryKey: BUSINESS_ADDRESS_KEYS.detail(id),
      });
    },
  });
};

export const useDeleteBusinessAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteBusinessAddress(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUSINESS_ADDRESS_KEYS.lists() });
    },
  });
};
